import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { ShoppingBag, Menu, Search, Heart, User, Home, Info, Phone, X, Star, HelpCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { motion, AnimatePresence } from "framer-motion";
import { fetchSiteSettings } from "@/lib/firebase";

const navItems = [
  { label: "Home", href: "/" },
  { label: "Shop", href: "/products" },
  { label: "About", href: "/about" },
  { label: "Testimonials", href: "/testimonials" },
  { label: "FAQ", href: "/faq" },
  { label: "Contact", href: "/contact" },
];

const mobileMenuItems = [
  { label: "Home", href: "/", icon: Home },
  { label: "Shop", href: "/products", icon: ShoppingBag },
  { label: "About Us", href: "/about", icon: Info },
  { label: "Testimonials", href: "/testimonials", icon: Star },
  { label: "FAQ", href: "/faq", icon: HelpCircle },
  { label: "Contact", href: "/contact", icon: Phone },
];

const mobileActionItems = [
  { label: "Search", href: "/products?focus=search", icon: Search },
  { label: "Cart", href: "/cart", icon: ShoppingBag },
  { label: "Wishlist", href: "/wishlist", icon: Heart },
  { label: "Account", href: "/account", icon: User },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [siteSettings, setSiteSettings] = useState({ brandName: "Trendy Quest", logoUrl: "" });
  const { totalItems } = useCart();
  const { wishlistCount } = useWishlist();
  const navRef = useRef(null);

  useEffect(() => {
    fetchSiteSettings().then(setSiteSettings).catch(() => {});
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const handlePointerDown = (event) => {
      if (navRef.current && !navRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open]);

  const linkVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.05, duration: 0.3 },
    }),
  };

  return (
    <div className="sticky top-0 z-50">
      <div className="h-1 w-full bg-green" />
      <motion.nav
        ref={navRef}
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative flex w-full min-w-0 items-center justify-between gap-2 border-b border-[#e8ddbf] bg-white/95 px-3 py-3 shadow-[0_10px_30px_rgba(20,68,37,0.08)] backdrop-blur-md sm:gap-3 sm:px-4 md:px-6 md:py-4"
      >
        <motion.div whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
          <Link href="/" className="group flex min-w-0 items-center gap-2 sm:gap-3">
            {siteSettings.logoUrl ? (
              <motion.div
                animate={{ rotate: [0, 3, -3, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="flex h-12 w-12 items-center justify-center rounded-2xl border border-[#e5d8b6] bg-[#fffdf7] p-2 shadow-[0_10px_24px_rgba(20,68,37,0.12)] sm:h-14 sm:w-14"
              >
                <div className="relative h-full w-full">
                  <Image
                    src={siteSettings.logoUrl}
                    alt={siteSettings.brandName}
                    fill
                    sizes="56px"
                    className="object-contain"
                  />
                </div>
              </motion.div>
            ) : (
              <motion.div
                animate={{ rotate: [0, 5, -5, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="flex h-12 w-12 items-center justify-center rounded-2xl bg-green text-lg font-bold text-white shadow-[0_10px_24px_rgba(20,68,37,0.16)]"
              >
                TQ
              </motion.div>
            )}
            <span className="hidden font-playfair text-xl font-bold text-[#184d26] transition-all group-hover:text-[#24552d] sm:inline">
              {siteSettings.brandName}
            </span>
          </Link>
        </motion.div>

        <div className="hidden items-center gap-8 md:flex">
          {navItems.map((item, i) => (
            <motion.div
              key={item.label}
              custom={i}
              variants={linkVariants}
              initial="hidden"
              animate="visible"
            >
              <Link
                href={item.href}
                className="group relative font-poppins font-semibold text-gray-700 transition-colors hover:text-green"
              >
                {item.label}
                <motion.div className="absolute bottom-0 left-0 h-0.5 w-0 bg-green transition-all group-hover:w-full" />
              </Link>
            </motion.div>
          ))}
        </div>

        <div className="flex items-center gap-3 sm:gap-4">
          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Link
              href="/products?focus=search"
              aria-label="Open search"
              className="hidden text-gray-700 transition-colors hover:text-green md:block"
            >
              <Search size={20} />
            </Link>
          </motion.div>

          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Link href="/cart" className="group relative">
              <motion.div
                animate={totalItems > 0 ? { y: [0, -3, 0] } : {}}
                transition={{ duration: 2, repeat: Infinity }}
              >
                <ShoppingBag className="h-6 w-6 text-gray-700 transition-colors group-hover:text-green" />
              </motion.div>
              <AnimatePresence>
                {totalItems > 0 && (
                  <motion.span
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    exit={{ scale: 0 }}
                    className="absolute -right-3 -top-3 flex h-6 w-6 items-center justify-center rounded-full bg-green text-xs font-bold text-white shadow-lg"
                  >
                    {totalItems}
                  </motion.span>
                )}
              </AnimatePresence>
            </Link>
          </motion.div>

          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Link
              href="/wishlist"
              aria-label="View wishlist"
              className="group relative hidden text-gray-700 transition-colors hover:text-green md:block"
            >
              <Heart size={20} />
              {wishlistCount > 0 && (
                <span className="absolute -right-3 -top-3 flex h-5 min-w-5 items-center justify-center rounded-full bg-green px-1 text-[10px] font-bold text-white shadow-lg">
                  {wishlistCount}
                </span>
              )}
            </Link>
          </motion.div>

          <motion.div whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.95 }}>
            <Link
              href="/account"
              aria-label="Open account page"
              className="hidden text-gray-700 transition-colors hover:text-green md:block"
            >
              <User size={20} />
            </Link>
          </motion.div>

          <a
            href="tel:+254717002340"
            className="hidden rounded-full border border-green bg-white px-5 py-3 text-sm font-semibold text-green transition hover:bg-green hover:text-white md:inline-flex"
          >
            Call Us
          </a>

          <Link
            href="/contact"
            className="hidden rounded-full bg-green px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#24552d] md:inline-flex"
          >
            Get in Touch
          </Link>

          <motion.button
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            aria-label="Open navigation menu"
            className="text-2xl text-gray-700 md:hidden"
            onClick={() => setOpen((current) => !current)}
          >
            {open ? <X /> : <Menu />}
          </motion.button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, x: -18 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -18 }}
              transition={{ duration: 0.22 }}
              className="absolute left-0 top-full z-40 w-full border border-[#d9d5cf] bg-white shadow-[0_18px_45px_rgba(20,68,37,0.16)] md:hidden"
            >
              <div className="grid grid-cols-4 border-b border-[#d9d5cf] bg-[#fbfaf7]">
                {mobileActionItems.map((item, i) => {
                  const Icon = item.icon;
                  const isCartItem = item.label === "Cart";
                  const isWishlistItem = item.label === "Wishlist";

                  return (
                    <motion.div
                      key={`top-${item.label}`}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: i * 0.03 }}
                      className="border-r border-[#d9d5cf] last:border-r-0"
                    >
                      <Link
                        href={item.href}
                        onClick={() => setOpen(false)}
                        className="flex flex-col items-center justify-center gap-2 px-2 py-4 text-[11px] font-medium uppercase tracking-[0.12em] text-[#3f3f3f] transition-colors hover:bg-white hover:text-green"
                      >
                        <span className="relative">
                          <Icon size={20} strokeWidth={1.9} />
                          {isCartItem && totalItems > 0 ? (
                            <span className="absolute -right-3 -top-3 flex h-5 min-w-5 items-center justify-center rounded-full bg-green px-1 text-[10px] font-bold text-white">
                              {totalItems}
                            </span>
                          ) : null}
                          {isWishlistItem && wishlistCount > 0 ? (
                            <span className="absolute -right-3 -top-3 flex h-5 min-w-5 items-center justify-center rounded-full bg-green px-1 text-[10px] font-bold text-white">
                              {wishlistCount}
                            </span>
                          ) : null}
                        </span>
                        <span>{item.label}</span>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>

              <div className="bg-white">
                {mobileMenuItems.map((item, i) => {
                  const Icon = item.icon;

                  return (
                    <motion.div
                      key={item.label}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ delay: i * 0.04 }}
                      className="border-b border-[#d9d5cf] last:border-b-0"
                    >
                      <Link
                        href={item.href}
                        onClick={() => setOpen(false)}
                        className="flex items-center gap-4 px-5 py-4 text-[1.08rem] font-medium text-[#161616] transition-colors hover:bg-[#faf8f4] hover:text-green"
                      >
                        <Icon size={22} className="shrink-0 text-[#4a4a4a]" strokeWidth={1.8} />
                        <span>{item.label}</span>
                      </Link>
                    </motion.div>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>
    </div>
  );
}
